import React from "react";

/**
 * AuroraWave — DEBSUN's signature motion language.
 * Emerald → teal → purple → gold ribbon rendered as three depth layers
 * (background / medium / foreground) with a soft glow, drifting sparkles,
 * and an optional slow gold pulse — calm, engineered, never flashy.
 *
 * Props:
 *  - speed   duration multiplier (2 = twice as fast, 0.5 = twice as slow)
 *  - pulse   render the occasional soft gold pulse (default off)
 */
export default function AuroraWave({ className = "", opacity = 0.9, variant = "band", sparkles = true, pulse = false, speed = 1 }) {
  const id = React.useId().replace(/:/g, "");
  const d1 = (18 / speed).toFixed(2);
  const d2 = (24 / speed).toFixed(2);
  const d3 = (30 / speed).toFixed(2);

  return (
    <svg
      className={className}
      viewBox="0 0 1440 320"
      preserveAspectRatio="none"
      fill="none"
      aria-hidden="true"
      style={{ opacity }}
    >
      <defs>
        <linearGradient id={`ag-${id}`} x1="0" y1="0" x2="1440" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#34E3B0" stopOpacity="0" />
          <stop offset="18%" stopColor="#34E3B0" />
          <stop offset="42%" stopColor="#00E5D4" />
          <stop offset="66%" stopColor="#8A5CF6" />
          <stop offset="86%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
        </linearGradient>
        <radialGradient id={`pulse-${id}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
        </radialGradient>
        {/* Background layer — softest, most diffuse */}
        <filter id={`glow-bg-${id}`} x="-20%" y="-80%" width="140%" height="260%">
          <feGaussianBlur stdDeviation="10" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        {/* Medium layer */}
        <filter id={`glow-mid-${id}`} x="-20%" y="-70%" width="140%" height="240%">
          <feGaussianBlur stdDeviation="6" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        {/* Foreground layer — crisp signature ribbon */}
        <filter id={`glow-fg-${id}`} x="-20%" y="-60%" width="140%" height="220%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Background — faint, slowest drift, widest blur */}
      <g filter={`url(#glow-bg-${id})`}>
        <g className="aurora-flow" style={{ animationDuration: `${d3}s` }}>
          <path
            d="M-200 140 C 120 200, 340 60, 600 150 S 1060 240, 1340 130 S 1720 60, 1980 160"
            stroke={`url(#ag-${id})`} strokeWidth="1.2" strokeOpacity="0.45"
          />
        </g>
      </g>

      {/* Medium — secondary ribbon */}
      <g filter={`url(#glow-mid-${id})`}>
        <g className="aurora-flow-2" style={{ animationDuration: `${d2}s` }}>
          <path
            d="M-200 200 C 160 140, 360 280, 620 200 S 1080 120, 1360 210 S 1720 280, 1960 190"
            stroke={`url(#ag-${id})`} strokeWidth={variant === "thin" ? 1.5 : 2} strokeOpacity="0.7"
          />
        </g>
      </g>

      {/* Foreground — the primary signature ribbon, crisp and closest */}
      <g filter={`url(#glow-fg-${id})`}>
        <g className="aurora-flow" style={{ animationDuration: `${d1}s` }}>
          <path
            d="M-200 170 C 100 90, 300 250, 560 160 S 1040 60, 1320 170 S 1700 250, 1900 150"
            stroke={`url(#ag-${id})`} strokeWidth={variant === "thin" ? 2 : 3.5} strokeLinecap="round"
          />
        </g>
      </g>

      {sparkles && (
        <g className="aurora-flow-2" style={{ animationDuration: `${d2}s` }}>
          {[[240,150],[520,175],[760,155],[1020,140],[1260,180],[400,130],[900,190]].map(([x,y],i)=>(
            <circle key={i} cx={x} cy={y} r={i % 2 ? 1.6 : 2.4}
              fill={i % 3 === 0 ? "#34E3B0" : i % 3 === 1 ? "#00E5D4" : "#D4AF37"}
              className="aurora-spark" style={{ animationDelay: `${i * 0.5}s` }} />
          ))}
        </g>
      )}

      {pulse && (
        <circle cx="880" cy="150" r="90" fill={`url(#pulse-${id})`} className="aurora-gold-pulse" />
      )}
    </svg>
  );
}

/** Vertical aurora thread that runs down the page connecting sections. */
export function AuroraThread({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 40 1000" preserveAspectRatio="none" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="thread" x1="20" y1="0" x2="20" y2="1000" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#34E3B0" stopOpacity="0" />
          <stop offset="30%" stopColor="#00E5D4" stopOpacity="0.55" />
          <stop offset="65%" stopColor="#8A5CF6" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d="M20 0 C 5 150, 35 300, 20 450 S 5 700, 20 850 S 30 950, 20 1000"
        stroke="url(#thread)" strokeWidth="1.5" />
    </svg>
  );
}
